import { useState, useEffect, useCallback, useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { TENANT_PLATFORM } from "@hiver/bifrost-js";

import { AutherContext, ToastContext } from "../Context";
import { getState, setState, STORAGE_KEYS, clearLocalStoragePreservingHotSession } from "../../Constant/LocalStorage";
import {
  userAuth,
  addUserToUserGroup,
  newHiverLoginInit,
  getLoggedInUserDetails,
  getHeimdallCodeHOT,
  getStoredAccessTokenHOT,
  validateTokenHOT
} from "../../Service/LoginServices";
import { open_popup, onOAuthSuccess } from "../../Pages/Onboarding/LoginPage/utils";
import { getAppConfig } from "../../Bifrost/api";
import { initConnectorsBifrost } from "../../Bifrost";
import { identifyGainsightUser } from "../../Constant/Utility";

// redirectPage not required. mocking setRedirectPage
const setRedirectPage = () => {};

const clearLocalStoragePreservingDefaultKb = () => {
  clearLocalStoragePreservingHotSession();
};

const stashReturnUrl = () => {
  const { pathname, search } = window.location;
  if (pathname !== "/login" && (search || pathname !== "/")) {
    sessionStorage.setItem("kb_return_url", pathname + search);
  }
};

export function NewAutherProvider(props) {
  const { setNoNet, setShowToast } = useContext(ToastContext);
  const [showError, setShowError] = useState(false);
  const [authers, setAuthers] = useState(false);
  const [admin, setAdmin] = useState(false);
  const [email, setEmail] = useState('');
  const [refetchToken, setRefetchToken] = useState(false);
  const [authToken, setAuthToken] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [userSession, setUserSession] = useState(false);
  const [redirectToken, setRedirectToken] = useState(true);
  const [userDetails, setUserDetails] = useState(false);
  const [tenant, setTenant] = useState(null);
  const navigate = useNavigate();

  const getGoogleAuthUrl = async ({email}) => {
    if (!email) {
      console.error('No Email Present');
      throw '';
    }

    const res = await newHiverLoginInit({ email, json_mode: 1, _csrf_token: undefined, force_login: 1 });
    if (res.status === 200) {
      return res?.data?.redirect_url;
    }
    throw new Error('Login init failed');
  }

  const initiateAuth = useCallback(({showMessage = true} = {}) => {
    getLoggedInUserDetails().then((response) => {
        const { data } = response || {};
        if (!data) return;
        setUserDetails(data);
        const { user_id, usergroup_id, tenantPlatform, getTokenForLogin } = data;
        const { appEnv, tier } = getAppConfig();
        initConnectorsBifrost( { 
          appEnv,
          tier,
          userId: user_id,
          userGroupId: usergroup_id,
          tenantPlatform,
          getTokenForLogin
        })
        setTenant(tenantPlatform || TENANT_PLATFORM.GMAIL);
        setAuthToken(true);
        identifyGainsightUser(user_id, usergroup_id);

        return addUserToUserGroup();
    }).catch( err => {
        //removing axios layer:
        throw err?.response;
    }).then(() => {
      setNoNet(false);
      return userAuth();       
    }).then((res) => {
      let user = res?.data?.user;
      setAdmin(user);
      setNoNet(false);
      setRedirectToken(true);
    })
    .catch((rej) => {
      const noNet = rej?.message === "Network Error";
      setNoNet(noNet);
      if(showMessage)setShowToast(rej?.data?.message);
      setAdmin(false); 
      if( rej?.status === 401 ){
        clearLocalStoragePreservingDefaultKb();
        stashReturnUrl();
        navigate("/login");
      }
    }).finally(()=>{
      setShowLoader(false);
    })
  }, [ setNoNet, setShowToast ] )

  /** Init Bifrost and auth state using validate_token payload (user_id, usergroup_id). Used after Other login. */
  const initiateAuthWithUserDetails = useCallback(({ user_id, usergroup_id }) => {
    const tenantPlatform = TENANT_PLATFORM.HIVERWEB;
    const data = { user_id, usergroup_id, tenantPlatform, getTokenForLogin: getHeimdallCodeHOT };
    setUserDetails(data);
    const { appEnv, tier } = getAppConfig();
    initConnectorsBifrost({
      appEnv,
      tier,
      userId: user_id,
      userGroupId: usergroup_id,
      tenantPlatform,
      getTokenForLogin: getHeimdallCodeHOT
    });
    setTenant(tenantPlatform);
    setAuthToken(true);
    identifyGainsightUser(user_id, usergroup_id);
    return addUserToUserGroup()
      .then(() => {
        setNoNet(false);
        return userAuth();
      })
      .then((res) => {
        const user = res?.data?.user;
        setAdmin(user);
        setNoNet(false);
        setRedirectToken(true);
      });
  }, [ setNoNet ] );

  /** Restore session when user returns to KB: if we have stored access-token, hit validate_token and init Bifrost. */
  const tryRestoreSessionFromStoredToken = useCallback(() => {
    const activeTenant = getState(STORAGE_KEYS.KB_ACTIVE_TENANT);
    if (activeTenant === TENANT_PLATFORM.GMAIL) return Promise.resolve(false);
    const accessToken = getStoredAccessTokenHOT();
    if (!accessToken) return Promise.resolve(false);
    return validateTokenHOT(accessToken)
      .then((res) => {
        const payloadData = res?.data?.data;
        if (payloadData?.id == null || payloadData?.accounts?.[0]?.id == null) return false;
        return initiateAuthWithUserDetails({
          user_id: payloadData.id,
          usergroup_id: payloadData.accounts[0].id
        }).then(() => true);
      })
      .catch(() => {
        localStorage.removeItem(STORAGE_KEYS.HOT_SESSION_DATA);
        localStorage.removeItem(STORAGE_KEYS.KB_ACTIVE_TENANT);
        return false;
      });
  }, [initiateAuthWithUserDetails]);

  const initiateLogin = ({email}) => {
    clearLocalStoragePreservingDefaultKb();
    return getGoogleAuthUrl({email}).then((gAuth) => {
      // initiateAuth passed instead of getTokenFunction as success callback:
      gAuth && open_popup(gAuth, onOAuthSuccess, email, setRefetchToken, setRedirectPage, setEmail, setShowError, initiateAuth);
    }).catch(e => console.error('Error while getting google auth: ', e));
  }


  useEffect(() => {
    const wasLoggedOut = sessionStorage.getItem("kb_logged_out");
    if (wasLoggedOut) {
      return;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const urlTenant = urlParams.get('tenant');
    if (urlTenant === TENANT_PLATFORM.GMAIL || urlTenant === TENANT_PLATFORM.HIVERWEB) {
      setState(STORAGE_KEYS.KB_ACTIVE_TENANT, urlTenant);
      urlParams.delete('tenant');
      const cleanSearch = urlParams.toString();
      const cleanUrl = window.location.pathname + (cleanSearch ? `?${cleanSearch}` : '') + window.location.hash;
      window.history.replaceState(null, '', cleanUrl);
    }
    const tenantFromUrl = urlTenant === TENANT_PLATFORM.GMAIL || urlTenant === TENANT_PLATFORM.HIVERWEB;
    tryRestoreSessionFromStoredToken().then((restored) => {
      if (restored) return;
      if (tenantFromUrl) {
        stashReturnUrl();
        navigate("/login");
      } else {
        initiateAuth({ showMessage: false });
      }
    });
  }, [tryRestoreSessionFromStoredToken, initiateAuth]);


  useEffect(() => {
    if (admin) setState("adminDetails", admin);
  }, [admin]);

  useEffect(() => {
    setState("authToken", authToken);
  }, [authToken]);

  const contextValue = useMemo(() => ({
    autherList: authers,
    updateAuthersList: setAuthers,
    admin: admin,
    tenant: tenant,
    setAdmin: setAdmin,
    setEmail: setEmail,
    email: email,
    authToken: authToken,
    setAuthToken: setAuthToken,
    redirectToken: redirectToken,
    setShowError,
    showError,
    setRedirectToken: setRedirectToken,
    userSession,
    setUserSession,
    refetchToken,
    setRefetchToken,
    setShowLoader,
    setRedirectPage,
    initiateLogin,
    initiateAuthWithUserDetails,
    showLoader,
    userDetails
  }), [
    authers, admin, tenant, email, authToken, redirectToken, showError, userSession,
    refetchToken, showLoader, userDetails,
    setShowError, setRedirectToken, setShowLoader,
    initiateLogin, initiateAuthWithUserDetails
  ]);

  return (
    <AutherContext.Provider value={contextValue}>
      {props.children}
    </AutherContext.Provider>
  );
}

NewAutherProvider.propTypes = {
  children: PropTypes.node
};
